import { useRecordContext, useGetMany, Button, NotFound } from "react-admin";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Chip, Divider, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import CustomFilter from "features/CustomFilter";
import { CampaignSubType, PAID, RankingStatus, RankingType, REGULAR } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import { administrationRequests } from "api/administrationApi";
import RankingResultsList from "./RankingResultsList";
import RankingSummary from "./RankingSummary";
import CampaignIcon from "@mui/icons-material/Campaign";
import ExportRankingButton from "components/Campaigns/components/ExportRankingButton";
import GenerateRankingButton from "components/Campaigns/components/GenerateRankingButton";
import ApproveRankingButton from "components/Campaigns/components/ApproveRankingButton";
import AdditionalCapacityButton from "components/Campaigns/components/AdditionalCapacityButton";
import { CandidateCampaignRankingTypeChoices } from "utils/choices";

const CampaignRankingResults = () => {
  const navigate = useNavigate();
  const record = useRecordContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const [specialityId, setSpecialityId] = useState("");
  const [shownSpecialityId, setShownSpecialityId] = useState("");

  const subTypeUrl = record?.campaignSubTypeId === CampaignSubType.Paid ? PAID : REGULAR;
  const campaignUrl = `/${candidateRequests.campaignByType(subTypeUrl)}/${record?.campaignId}/show`;
  const rankingUrl = candidateRequests.campaignRanking(record?.id);

  const { data: specialities } = useGetMany(administrationRequests.getSpecialitiesSelect(), { ids: record?.specialityIds, filterByUser: true });

  useEffect(() => {
    const queryParamSpecialtyId = +searchParams.get("specialityId");

    if (specialities?.some(s => s.id === queryParamSpecialtyId)) {
      setShownSpecialityId(queryParamSpecialtyId);
      setSpecialityId(queryParamSpecialtyId);
    }
  }, [searchParams, specialities]);

  if (!record || !specialities) {
    return null;
  }

  if (record.status === RankingStatus.Pending) {
    return <NotFound />;
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        my={2}
        gap={3}
      >
        <Typography
          variant="h6"
          align="center"
          color={"secondary"}
        >
          Класиране <i>№{record?.rankingNumber}</i> от <i>{record?.campaignName}</i>
        </Typography>
        <Chip
          label={CandidateCampaignRankingTypeChoices.find(choice => choice.id === record?.type)?.name}
          color={record?.type === RankingType.Paid ? "warning" : "info"}
          size="small"
        />
        <Button
          onClick={() => navigate(campaignUrl)}
          label="Кампания"
          size="medium"
          variant="outlined"
        >
          <CampaignIcon />
        </Button>
      </Box>
      <Box
        display={"flex"}
        justifyContent="center"
        alignItems={"center"}
        gap={2}
        flexDirection={record.status === RankingStatus.Approved ? "column" : "row"}
      >
        {record.status === RankingStatus.Draft ? (
          <>
            <GenerateRankingButton />
            <ApproveRankingButton specialities={specialities} />
          </>
        ) : (
          <Typography
            variant="subtitle1"
            sx={{ fontStyle: "italic" }}
            align="center"
            color="secondary"
          >
            {`Одобрено класиране`}
          </Typography>
        )}
        <Box
          display="flex"
          gap={2}
        >
          <ExportRankingButton />
          <AdditionalCapacityButton />
        </Box>
      </Box>
      <Divider
        variant="middle"
        orientation="horizontal"
        sx={{ marginY: 2 }}
      />
      <CustomFilter
        title="Избор на специалност"
        filters={[
          {
            label: "Специалност",
            value: specialities?.some(s => s.id === +specialityId) ? specialityId : "",
            setValue: v => setSpecialityId(v),
            type: "select",
            multiple: false,
            choices: specialities,
          },
        ]}
        cardWidth="700px"
        buttonLabel="Покажи"
        buttonDisabled={!specialityId || (specialityId && specialityId === shownSpecialityId)}
        buttonOnClick={() => {
          setShownSpecialityId(specialityId);
          setSearchParams(params => {
            params.set("specialityId", specialityId);
            return params;
          });
        }}
      />
      <Divider
        variant="middle"
        orientation="horizontal"
        sx={{ marginY: 2 }}
      />
      <Box my={3}>
        <Typography
          variant="inherit"
          align="center"
          color={"secondary"}
        >
          {shownSpecialityId
            ? `Резултати от класиране за специалност ${specialities.find(s => s.id === +shownSpecialityId)?.name}`
            : "Изберете специалност, за да видите резултатите от класирането."}
        </Typography>
      </Box>
      {shownSpecialityId && (
        <>
          <Divider
            variant="middle"
            orientation="horizontal"
            sx={{ marginY: 2 }}
          />
          <RankingSummary
            specialityId={shownSpecialityId}
            rankingUrl={rankingUrl}
          />
          <RankingResultsList
            specialityId={shownSpecialityId}
            rankingUrl={rankingUrl}
          />
        </>
      )}
    </>
  );
};

export default CampaignRankingResults;
